<template>
  <div class="page">
    <div class="heading">
      <h1 class="inline">Users</h1>
    </div>
    <VTextField v-model="q" placeholder="Search users..." class="mb-1 mt-1" />
    <VTable
      v-model:limit="limit"
      v-model:offset="offset"
      v-model:sort-by="sortBy"
      v-model:sort-dir="sortDir"
      v-model:total="total"
      :headers="headers"
      :items="users"
      description="Table of users"
    >
      <template #[`item.avatar`]="{ item }">
        <VAvatar :src="avatarThumbnail(item)" :text="item.name" />
      </template>
      <template #[`item.name`]="{ item }">
        <NullableField v-model="item.name" />
      </template>
      <template #[`item.role`]="{ item }">
        <VChip v-text="roleText(item.role)" />
      </template>
      <template #[`item.email`]="{ item }">
        <NullableField v-model="item.email" />
      </template>
    </VTable>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted, watch } from "vue";
import NullableField from "../components/NullableField.vue";
import WorkspaceUser from "../models/user";
import User from "../models/user";
import QueryContext from "../models/queryContext";
import { snakecase, titlecase } from "stringcase";
import VAvatar from "../components/VAvatar.vue";
import VChip from "../components/VChip.vue";
import VTable from "../components/VTable.vue";
import VTextField from "../components/VTextField.vue";
import WorkspaceApi from "../api/workspace";

const props = defineProps<{
  slug: string;
}>();

// Reactive data
const debounceTimer = ref(setTimeout(() => "", 500));
const headers = ref([
  { text: "", value: "avatar", sortable: false },
  { text: "Name", value: "name" },
  { text: "Role", value: "role" },
  { text: "E-mail", value: "email" },
]);
const limit = ref(10);
const offset = ref(0);
const sortBy = ref([]);
const sortDir = ref([]);
const q = ref("");
const total = ref(0);
const users = ref([]);

// Methods
const avatarThumbnail = (user) => {
  return !!user.avatar ? user.avatar.thumbnailUrl : undefined;
};

const fetchAllUsers = async () => {
  const context = new QueryContext(
    limit.value,
    offset.value,
    sortBy.value.map(snakecase),
    sortDir.value,
    q.value,
  );
  let response = await WorkspaceApi.allUsers(props.slug, context);
  users.value = response.data.users as WorkspaceUser[];
  total.value = response.data.pagination.total;
};

const roleText = (role) => {
  return titlecase(role);
};

// Watchers
watch(limit, () => {
  fetchAllUsers();
});

watch(offset, () => {
  fetchAllUsers();
});

watch(sortBy, () => {
  offset.value = 0;
  fetchAllUsers();
});

watch(sortDir, () => {
  offset.value = 0;
  fetchAllUsers();
});

watch(q, () => {
  clearTimeout(debounceTimer.value);

  debounceTimer.value = setTimeout(() => {
    offset.value = 0;
    fetchAllUsers();
  }, 500);
});

// Lifecycle hooks
onMounted(() => {
  fetchAllUsers();
});
</script>
